import { useState } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";

// Phase 40 (C2.1) — firm-wide Peppol/UBL XML template editor. Stored on
// FirmSettings.proformaHtmlTemplate (reused; the old client-side HTML pro-forma
// template is dead post-Part-2). ONE firm-wide template — language is carried by
// the DATA, not by variants. The author writes UBL/Peppol XML with the SAME
// {marker} syntax as the Word invoice template, including {#entries}…{/entries}
// for the repeating invoice lines. Well-formedness is validated on SAVE (markers
// are plain text/attribute values, so the raw template parses as XML); a
// structurally-broken template is REJECTED and never stored.

/* 170 §3f [MEASURED] — THE HAND-COPY IS GONE. This file carried 37 marker
   names transcribed from `docGenVariables.js` INVOICE_SCALARS, which holds 42:
   it was missing `docTitle`, `feesLabel`, `hasCommercialDeduction`,
   `hasRetainerDeduction` and `currency`. The panel's own copy in SettingsView
   had drifted the other way, carrying five markers the resolver does not emit.
   Two copies, two different wrong answers, one registry that was right all
   along and already served at GET /api/doc-templates/variables.
   The list now arrives as a prop from the one caller that fetches it, so this
   file cannot drift again and a fourth copy has nowhere to start. */

type XmlValidation = { ok: boolean; msg: string };

// Parse a template as XML, treating {markers} as ordinary text. Returns
// { ok:true } or { ok:false, msg }. An empty template is "ok" (= no template).
export function validateXmlWellFormed(xml: string): XmlValidation {
  if (!xml || !xml.trim()) return { ok: true, msg: "empty" };
  try {
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    const err = doc.getElementsByTagName("parsererror");
    if (err && err.length) {
      const first = (err[0].textContent || "not well-formed XML")
        .split("\n").map((s) => s.trim()).filter(Boolean)[0] || "not well-formed XML";
      return { ok: false, msg: first };
    }
    return { ok: true, msg: "well-formed" };
  } catch (e) {
    return { ok: false, msg: (e && (e as any).message) || "parse error" };
  }
}

type ProformaXmlTemplateEditorProps = {
  value?: string;
  onSave: (xml: string) => void;
  toast?: (msg: string, kind?: string) => void;
  /** 170 §3f — the merge-marker vocabulary, from `GET /doc-templates/variables`.
   *  Owned by the caller so there is exactly one fetch and one list. */
  markers?: string[];
};

export default function ProformaXmlTemplateEditor({ value, onSave, toast, markers = [] }: ProformaXmlTemplateEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value || "");
  const [status, setStatus] = useState<XmlValidation | null>(null); // { ok, msg }

  const handleSave = () => {
    const r = validateXmlWellFormed(draft);
    if (!r.ok) {
      setStatus(r);
      toast &&
        toast(
          `⚠ ${t("settings.xmlTpl.invalid", { fallback: "Not well-formed XML" })}: ${r.msg}`,
          "error",
        );
      return;
    }
    setStatus(r);
    onSave(draft);
    toast && toast(t("settings.xmlTpl.saved", { fallback: "Peppol XML template saved" }));
  };

  return (
    <div style={{ borderBottom: "1px solid var(--br)", paddingBottom: 10, marginBottom: 10 }}>
      <div style={{ fontSize: FS.s10, fontWeight: 600, color: "var(--tp)", marginBottom: 6 }}>
        {t("settings.xmlTpl.title", { fallback: "Peppol XML template (UBL)" })}
      </div>
      <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: "0 0 6px", lineHeight: 1.5 }}>
        {t("settings.xmlTpl.help", {
          fallback:
            "One firm-wide UBL/Peppol XML body, filled from the SAME invoice data as the PDF (the language is in the data — no separate copies). Use the invoice {markers} below; wrap the repeating invoice lines in {#entries}…{/entries}. Validated as well-formed XML on save; emitted alongside the PDF on Send to client.",
        })}
      </p>
      <textarea
        className="field-input"
        rows={10}
        style={{ resize: "vertical", fontSize: FS.s10, fontFamily: "'JetBrains Mono',monospace" }}
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setStatus(null); }}
        placeholder={'<?xml version="1.0" encoding="UTF-8"?>\n<Invoice>…<cbc:ID>{invoiceNumber}</cbc:ID>…{#entries}<cac:InvoiceLine>…{description}…</cac:InvoiceLine>{/entries}…</Invoice>'}
      />
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
        <button type="button" className="btn btn-primary" style={{ padding: "4px 10px", fontSize: FS.s10 }} onClick={handleSave}>
          {t("settings.xmlTpl.save", { fallback: "Save XML template" })}
        </button>
        {status && (
          <span style={{ fontSize: FS.s10, color: status.ok ? "var(--gn)" : "var(--rd)" }}>
            {status.ok ? "✓ " : "✗ "}{status.msg}
          </span>
        )}
      </div>
      <div style={{ fontSize: FS.s9_5, color: "var(--ts)", marginTop: 6, lineHeight: 1.6 }}>
        <span style={{ fontWeight: 600 }}>
          {t("settings.xmlTpl.markers", { fallback: "Markers" })}:{" "}
        </span>
        {markers.map((m) => `{${m}}`).join("  ")}
        {"  · "}
        <code>{"{#entries}{date} {type} {description} {time}{/entries}"}</code>
      </div>
    </div>
  );
}
